// ═══════════════════════════════════════════════════════════════════════
// PRICING — single source of truth for all displayed prices
// ═══════════════════════════════════════════════════════════════════════
// IMPORTANT: this must match the Stripe Price IDs configured in backend .env
// (STRIPE_PRICE_GBP_MONTHLY etc). Mismatched prices = UI lies to customer.
//
// CURRENT STATE: Real production prices, using Stripe TEST MODE keys/prices.
// Sign up flow uses test cards (4242 4242 4242 4242) but shows real £/AED
// amounts so the UX matches what real customers will see.
//
// When ready for prod launch:
//   - Swap server-side .env from sk_test/whsec_test to sk_live/whsec_live
//   - Swap the 8 STRIPE_PRICE_*_* env vars to live price IDs at same amounts
//   - No frontend change needed (prices stay the same)
// ═══════════════════════════════════════════════════════════════════════
const PRICING_MODE = "PROD"; // Real-world prices for both test mode and live

export const PRICING = {
  TEST: {
    // Legacy 1p amounts — kept for reference only.
    GBP: { firstSite: 0.01,  additional: 0.005, currencySymbol: "£" },
    AED: { firstSite: 0.046, additional: 0.023, currencySymbol: "AED " },
  },
  PROD: {
    // Real-world prices. MUST match the Stripe price IDs in backend .env.
    // GBP: First £49.99/mo, Additional £39.99/mo
    // Monthly: GBP £49.99 first / £39.99 add'l; AED 250 first / 195 add'l; USD $64.99 first / $51.99 add'l; INR FLAT ₹3,999 (first = add'l).
    // Annual = pay for the year, get ONE MONTH FREE (= 11× the monthly rate).
    // These MUST equal the live Stripe unit_amounts (quoted price = charged price):
    //   GBP first 49.99/549.89, add'l 39.99/439.89; AED first 250/2750, add'l 195/2145;
    //   USD first 64.99/714.89, add'l 51.99/571.89; INR flat 3999/43989 (both tiers).
    GBP: { firstSite: 49.99, firstSiteAnnual: 549.89, additional: 39.99, additionalAnnual: 439.89, currencySymbol: "£" },
    AED: { firstSite: 250,   firstSiteAnnual: 2750,   additional: 195,   additionalAnnual: 2145,   currencySymbol: "AED " },
    // USD launch pricing — PLACEHOLDER pending owner confirmation; must match the Stripe USD prices.
    // Derived as GBP × 1.3 keeping the GBP conventions (.99 monthly endings; annual = exactly 11× monthly).
    USD: { firstSite: 64.99, firstSiteAnnual: 714.89, additional: 51.99, additionalAnnual: 571.89, currencySymbol: "$" },
    // INR pricing — owner-confirmed 2026-08-08: FLAT ₹3,999 per workspace
    // (first and additional sites cost the SAME in India, unlike other
    // markets). Annual = exactly 11× monthly (one month free). Must match the
    // Stripe INR prices (whole-rupee amounts).
    INR: { firstSite: 3999,  firstSiteAnnual: 43989,  additional: 3999,  additionalAnnual: 43989,  currencySymbol: "₹" },
  },
};

export const PRICE = PRICING[PRICING_MODE];

// Helpers — use these throughout the UI instead of hardcoded amounts
export function getFirstSiteRate(currency) {
  return PRICE[currency || "GBP"]?.firstSite ?? PRICE.GBP.firstSite;
}
export function getAdditionalRate(currency) {
  return PRICE[currency || "GBP"]?.additional ?? PRICE.GBP.additional;
}
// Annual = pay for the year, one month free (≈11× monthly).
export function getFirstSiteAnnual(currency) {
  return PRICE[currency || "GBP"]?.firstSiteAnnual ?? PRICE.GBP.firstSiteAnnual;
}
export function getAdditionalAnnual(currency) {
  return PRICE[currency || "GBP"]?.additionalAnnual ?? PRICE.GBP.additionalAnnual;
}
export function getCurrencySymbol(currency) {
  return PRICE[currency || "GBP"]?.currencySymbol ?? "£";
}
// Total monthly for N workspaces (1× first + (N-1)× additional)
export function calcMonthlyTotal(currency, workspaceCount) {
  if (workspaceCount <= 0) return 0;
  const first = getFirstSiteRate(currency);
  const additional = getAdditionalRate(currency);
  return first + Math.max(0, workspaceCount - 1) * additional;
}
// Total ANNUAL for N workspaces (1× first-annual + (N-1)× additional-annual).
// The annual rates already bake in "one month free" (≈11× monthly), so this is
// the single source of truth for the annual price shown anywhere in the UI —
// never multiply the monthly total by 10/11/12 by hand.
export function calcAnnualTotal(currency, workspaceCount) {
  if (workspaceCount <= 0) return 0;
  const first = getFirstSiteAnnual(currency);
  const additional = getAdditionalAnnual(currency);
  return first + Math.max(0, workspaceCount - 1) * additional;
}

// Legacy alias — some old code still references WS_PRICE. Points at GBP first-site.
export const WS_PRICE = getFirstSiteRate("GBP");
